import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Link } from 'react-scroll';

export default function ProgramsPage() {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('pre-incubation');
  const [selectedPhase, setSelectedPhase] = useState(1);

  useEffect(() => {
    if (location.state?.tab) {
      setActiveTab(location.state.tab);
    }
  }, [location.state]);

  useEffect(() => {
    // Check if AOS is loaded from the window (index.html)
    if (window.AOS) {
      window.AOS.init({
        duration: 800,
        once: true,
        easing: 'ease-out-cubic'
      });
    }
  }, []);

  const phases = [
    { 
      id: 1, 
      name: 'IDEATION', 
      subtitle: 'Development Phase',
      image: 'https://cdn.imgchest.com/files/d2cb8f3518a3.png', 
      description: [
        "Define the core problem your startup aims to solve and begin conceptualizing viable solutions. You'll work closely with mentors to refine your raw ideas into structured concepts.",
        "During this phase, you will draft your initial Business Model Canvas and conduct preliminary market research to validate your target audience and unique value proposition."
      ]
    },
    { 
      id: 2, 
      name: 'LEARNING', 
      subtitle: 'Educational Phase',
      image: 'https://cdn.imgchest.com/files/06bdbc8cbc4e.png',
      description: [
        "Participate in a comprehensive series of workshops and seminars tailored for early-stage founders. Topics cover essential startup fundamentals, including product development, digital marketing, and legal compliance.",
        "Gain invaluable insights from industry experts and experienced entrepreneurs. This phase is designed to equip your team with the technical and business skills required to build a solid foundation."
      ]
    },
    { 
      id: 3, 
      name: 'VALIDATION', 
      subtitle: 'Testing Phase',
      image: 'https://cdn.imgchest.com/files/0967f824f47b.png',
      description: [
        "Translate your concepts into reality by developing your Minimum Viable Product (MVP). You will bring your early build directly to your target market to observe real-world interactions.",
        "Conduct extensive user testing, gather critical feedback, and iterate on your prototype. This ensures your solution achieves product-market fit before you invest heavily in scaling."
      ]
    },
    { 
      id: 4, 
      name: 'DEMO DAY', 
      subtitle: 'Presentation Phase',
      image: 'https://cdn.imgchest.com/files/6ba3d57c749f.png',
      description: [
        "Prepare to showcase your startup's growth, market potential, and refined product. You will undergo rigorous pitch training to confidently present your venture.",
        "Deliver your final pitch deck to a curated panel of investors, industry leaders, and potential partners. This is your primary opportunity to secure early-stage funding and strategic relationships."
      ]
    },
    { 
      id: 5, 
      name: 'GRADUATION', 
      subtitle: 'Completion Phase',
      image: 'https://cdn.imgchest.com/files/8bd747a1107e.png',
      description: [
        "Celebrate the milestones achieved throughout the six-month Dagitab Program. You will officially transition from an incubatee to a recognized startup ready to tackle the broader market.",
        "Join our exclusive alumni network to receive ongoing support, networking opportunities, and continuous guidance as you independently scale your business operations."
      ]
    }
  ];

  const renderPrograms = () => {
    if (activeTab === 'pre-incubation') {
      return (
        <>
          {/* SINDI Program */}
          <div className="flex flex-col md:flex-row gap-4 sm:gap-6 md:gap-8 items-start" data-aos="fade-up" data-aos-delay="100">
            <div className="w-full md:w-80 flex-shrink-0 rounded-xl md:rounded-2xl overflow-hidden shadow-lg" data-aos="zoom-in" data-aos-delay="200">
              <img
                src="/images/img-3.png"
                alt="SINDI Program"
                className="w-full h-48 sm:h-64 md:h-auto object-cover"
              />
            </div>
            <div className="flex-1" data-aos="fade-left" data-aos-delay="300">
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">
                SINDI: Shaping Innovators & New Ventures through Design Ideation
              </h3>
              <p className="text-xs sm:text-sm text-gray-600 mb-4 sm:mb-6 leading-relaxed">
                Programs crafted to help founders and future innovators to validate ideas, grow their ventures, 
                enhance tech capabilities, and expand their networks.
              </p>
              <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
                  <div className="flex items-start gap-2 sm:gap-3 flex-1">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-xs sm:text-sm font-semibold text-gray-900">Coming Soon</p>
                  </div>
                  <div className="flex items-center gap-2 ml-6 sm:ml-0">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-xs sm:text-sm text-gray-700">TBA</p>
                  </div>
                </div>
                <div className="flex items-start gap-2 sm:gap-3">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <p className="text-xs sm:text-sm text-gray-700">ICTO Computer Laboratory, PUP Sta. Mesa</p>
                </div>
              </div>
              <p className="text-xs sm:text-sm text-gray-500 mb-3 sm:mb-4">
                Stay tuned on our Facebook page for the latest news!
              </p>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <Link to="contact" onClick={() => window.dispatchEvent(new Event('highlight-footer'))} className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-2.5 border-2 border-gray-400 text-gray-700 rounded-full font-medium hover:border-gray-500 hover:bg-gray-50 transition text-xs sm:text-sm cursor-pointer text-center">
                  Contact Us
                </Link>
                <a 
                  href="#"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-2.5 rounded-full font-medium transition text-xs sm:text-sm text-white text-center"
                  style={{
                    borderImage: 'linear-gradient(90deg, rgba(109,12,34,1) 0%, rgba(14,56,107,1) 76%) 1',
                    background: 'linear-gradient(90deg, rgba(109,12,34,1) 0%, rgba(14,56,107,1) 76%)',
                  }}
                >
                  Learn More
                </a>
              </div>
            </div>
          </div>

          {/* CALL FOR COHORT II */}
          <div className="flex flex-col md:flex-row gap-4 sm:gap-6 md:gap-8 items-start" data-aos="fade-up" data-aos-delay="400">
            <div className="w-full md:w-80 flex-shrink-0 rounded-xl md:rounded-2xl overflow-hidden shadow-lg" data-aos="zoom-in" data-aos-delay="500">
              <img
                src="/images/call-for-cohort-3.png"
                alt="Cohort II"
                className="w-full h-48 sm:h-64 md:h-auto object-cover"
              />
            </div>
            <div className="flex-1" data-aos="fade-left" data-aos-delay="600">
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">
                CALL FOR COHORT III
              </h3>
              <p className="text-xs sm:text-sm text-gray-600 mb-4 sm:mb-6 leading-relaxed">
                Programs crafted to help founders and future innovators to validate ideas, grow their ventures, 
                enhance tech capabilities, and expand their networks.
              </p>
              <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
                  <div className="flex items-start gap-2 sm:gap-3 flex-1">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-xs sm:text-sm font-semibold text-gray-900">Coming Soon</p>
                  </div>
                  <div className="flex items-center gap-2 ml-6 sm:ml-0">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-xs sm:text-sm text-gray-700">TBA</p>
                  </div>
                </div>
                <div className="flex items-start gap-2 sm:gap-3">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <p className="text-xs sm:text-sm text-gray-700">TBIDO Office</p>
                </div>
              </div>
              <p className="text-xs sm:text-sm text-gray-500 mb-3 sm:mb-4">
                Stay tuned on our Facebook page for the latest news!
              </p>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <Link to="contact" onClick={() => window.dispatchEvent(new Event('highlight-footer'))} className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-2.5 border-2 border-gray-400 text-gray-700 rounded-full font-medium hover:border-gray-500 hover:bg-gray-50 transition text-xs sm:text-sm cursor-pointer text-center">
                  Contact Us
                </Link>
                <a target="_blank" href="https://www.facebook.com/DOSTPUPPYLONTBI"
                  className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-2.5 rounded-full font-medium transition text-xs sm:text-sm text-white text-center"
                  style={{
                    borderImage: 'linear-gradient(90deg, rgba(109,12,34,1) 0%, rgba(14,56,107,1) 76%) 1',
                    background: 'linear-gradient(90deg, rgba(109,12,34,1) 0%, rgba(14,56,107,1) 76%)',
                  }}
                >
                  Learn More
                </a>
              </div>
            </div>
          </div>
        </>
      );
    }

    if (activeTab === 'incubation') {
      return (
        <div className="w-full" data-aos="fade-up">
          {/* Dagitab Program Header with Gradient */}
          <div className="text-center mb-6 sm:mb-8 md:mb-10" data-aos="fade-down" data-aos-delay="100">
            <h3 
              className="text-2xl sm:text-3xl md:text-4xl py-1 font-bold mb-2 sm:mb-3 md:mb-4 text-transparent bg-clip-text"
              style={{
                backgroundImage: 'linear-gradient(90deg, rgba(109,12,34,1) 0%, rgba(14,56,107,1) 76%)',
              }}
            >
              Dagitab Program
            </h3>
            <p className="text-xs sm:text-sm md:text-base text-gray-600 max-w-xl mx-auto px-4">
              The Dagitab Program is a six-month incubation program for aspiring innovators, early-stage startups and faculty technopreneurs.
            </p>
          </div>

          {/* 5-Phase Progress Bar */}
          <div className="mb-8 sm:mb-12 md:mb-16 w-full overflow-x-auto hide-scroll pb-4" data-aos="fade-up" data-aos-delay="200" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <div className="flex items-center justify-between min-w-[500px] sm:min-w-0 max-w-3xl mx-auto relative px-4">
              {/* Background Line - Desktop only */}
              <div className="absolute top-[20px] sm:top-[28px] md:top-[32px] left-8 right-8 h-0.5 sm:h-1 bg-gray-300 -z-10"></div>
              
              {/* Progress Line with Gradient - Desktop only*/}
              <div 
                className="absolute top-[20px] sm:top-[28px] md:top-[32px] left-8 h-0.5 sm:h-1 -z-10 transition-all duration-500"
                style={{
                  width: `calc(${((selectedPhase - 1) / 4) * 100}% - 2rem)`,
                  background: 'linear-gradient(90deg, rgba(109,12,34,1) 0%, rgba(14,56,107,1) 76%)',
                  boxShadow: '0 0 10px rgba(14,56,107,0.5)'
                }}
              ></div>
              
              {phases.map((phase, index) => (
                <div 
                  key={phase.id}
                  className="flex flex-col items-center flex-1 cursor-pointer group px-2" 
                  onClick={() => setSelectedPhase(phase.id)}
                  data-aos="zoom-in"
                  data-aos-delay={300 + (index * 100)}
                >
                  <div 
                    className={`w-10 h-10 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center mb-1.5 sm:mb-2 md:mb-3 transition-all duration-300 ${
                      selectedPhase >= phase.id 
                        ? 'shadow-lg' 
                        : 'bg-gray-300'
                    }`}
                    style={selectedPhase >= phase.id ? {
                      background: 'linear-gradient(135deg, rgba(109,12,34,1) 0%, rgba(14,56,107,1) 100%)',
                      boxShadow: '0 0 20px rgba(14,56,107,0.6), 0 0 40px rgba(14,56,107,0.3)'
                    } : {}}
                  >
                    {/* Icons for each phase */}
                    {phase.id === 1 && (
                      <svg className="w-5 h-5 sm:w-7 sm:h-7 md:w-8 md:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                    )}
                    {phase.id === 2 && (
                      <svg className="w-5 h-5 sm:w-7 sm:h-7 md:w-8 md:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                    )}
                    {phase.id === 3 && (
                      <svg className="w-5 h-5 sm:w-7 sm:h-7 md:w-8 md:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12" />
                      </svg>
                    )}
                    {phase.id === 4 && (
                      <svg className="w-5 h-5 sm:w-7 sm:h-7 md:w-8 md:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    )}
                    {phase.id === 5 && (
                      <svg className="w-5 h-5 sm:w-7 sm:h-7 md:w-8 md:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                      </svg>
                    )}
                  </div>
                  <p 
                    className={`text-[10px] md:text-xs font-bold text-center mb-0.5 sm:mb-1 leading-tight ${
                      selectedPhase >= phase.id ? 'text-[#0E386B]' : 'text-gray-400'
                    }`}
                  >
                    {phase.id}. {phase.name}
                  </p>
                  <p className="text-[9px] md:text-xs text-gray-500 text-center leading-tight">
                    {phase.subtitle}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Phase Details Card */}
          <div className="flex flex-col md:flex-row gap-4 sm:gap-6 md:gap-8 bg-white border border-gray-200 md:border-2 rounded-xl md:rounded-2xl p-4 sm:p-6 md:p-8" data-aos="fade-up" data-aos-delay="800">
            {/* Left Side - Phase Number, Title and Description */}
            <div className="flex-1">
              <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-5 md:mb-6">
                <div 
                  className="px-4 py-2 sm:px-5 sm:py-2.5 md:px-6 md:py-3 rounded-lg font-bold text-lg sm:text-xl md:text-2xl flex-shrink-0 text-white"
                  style={{
                    background: 'linear-gradient(135deg, rgba(109,12,34,1) 0%, rgba(14,56,107,1) 100%)',
                  }}
                >
                  0{selectedPhase}
                </div>
                <div className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-2 flex-wrap">
                  <h4 className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-gray-900">
                    {phases[selectedPhase - 1].name.charAt(0) + phases[selectedPhase - 1].name.slice(1).toLowerCase()}
                  </h4>
                  <p className="text-xs sm:text-sm text-gray-600">{phases[selectedPhase - 1].subtitle}</p>
                </div>
              </div>

              {/* Description Text */}
              <div className="space-y-2 sm:space-y-3 md:space-y-4">
                {phases[selectedPhase - 1].description.map((desc, idx) => (
                   <p key={idx} className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                     {desc}
                   </p>
                ))}
              </div>
            </div>
            
            {/* Right Side - Phase Image */}
            <div className="w-full md:w-80 h-48 sm:h-56 md:h-64 lg:h-80 flex-shrink-0 overflow-hidden rounded-lg md:rounded-xl">
              <img 
                src={phases[selectedPhase - 1].image} 
                alt={phases[selectedPhase - 1].name} 
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      );
    }

    if (activeTab=== 'networking'){
      return (
        <>
        <div className="flex flex-col md:flex-row gap-4 sm:gap-6 md:gap-8 items-start" data-aos="fade-up" data-aos-delay="400">
          <div className="w-full md:w-80 flex-shrink-0 rounded-xl md:rounded-2xl overflow-hidden shadow-lg" data-aos="zoom-in" data-aos-delay="500">
            <img
              src="/images/pup-pylon-awards-night.jpg"
              alt="Awards-Night"
              className="w-full h-48 sm:h-64 md:h-auto object-cover"
            />
          </div>
          <div className="flex-1" data-aos="fade-left" data-aos-delay="600">
            <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">
              PUP PYLON AWARDS NIGHT
            </h3>
            <p className="text-xs sm:text-sm text-gray-600 mb-4 sm:mb-6 leading-relaxed">
              We proudly invite all PUP alumni to join us for an inspiring evening of innovation,
              recognition, and meaningful connections at the Pylon Awards Night!
            </p>
            <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
              <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
                <div className="flex items-start gap-2 sm:gap-3 flex-1">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-xs sm:text-sm font-semibold text-gray-900">May 14, 2026</p>
                </div>
                <div className="flex items-center gap-2 ml-6 sm:ml-0">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-xs sm:text-sm text-gray-700">Anytime</p>
                </div>
              </div>
              <div className="flex items-start gap-2 sm:gap-3">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <p className="text-xs sm:text-sm text-gray-700">Bulwagang Balagtas, 4th Floor NALLRC, PUP Sta. Mesa, Manila</p>
              </div>
            </div>
            <p className="text-xs sm:text-sm text-gray-500 mb-3 sm:mb-4">
              Visit our Facebook page for the latest news!
            </p>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <Link to="contact" onClick={() => window.dispatchEvent(new Event('highlight-footer'))} className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-2.5 border-2 border-gray-400 text-gray-700 rounded-full font-medium hover:border-gray-500 hover:bg-gray-50 transition text-xs sm:text-sm cursor-pointer text-center">
                Contact Us
              </Link>
              <a target="_blank" href="https://web.facebook.com/DOSTPUPPYLONTBI/posts/pfbid02TUv9xiPHWkmuVzJaLLWGdrrgERZJmXfqUQedLunSbftNZBrYRqU2uJsEq6EiRo7ol"
                className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-2.5 rounded-full font-medium transition text-xs sm:text-sm text-white text-center"
                style={{
                  borderImage: 'linear-gradient(90deg, rgba(109,12,34,1) 0%, rgba(14,56,107,1) 76%) 1',
                  background: 'linear-gradient(90deg, rgba(109,12,34,1) 0%, rgba(14,56,107,1) 76%)',
                }}
              >
                Learn More
              </a>
            </div>
          </div>
        </div>
        <div className="flex flex-col md:flex-row gap-4 sm:gap-6 md:gap-8 items-start" data-aos="fade-up" data-aos-delay="400">
          <div className="w-full md:w-80 flex-shrink-0 rounded-xl md:rounded-2xl overflow-hidden shadow-lg" data-aos="zoom-in" data-aos-delay="500">
            <img
              src="/images/pup-pylon-awards-call.jpg"
              alt="Call for Nominations"
              className="w-full h-48 sm:h-64 md:h-auto object-cover"
            />
          </div>
          <div className="flex-1" data-aos="fade-left" data-aos-delay="600">
            <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">
              PUP PYLON AWARDS: Call for Nominations
            </h3>
            <p className="text-xs sm:text-sm text-gray-600 mb-4 sm:mb-6 leading-relaxed">
              Submit your nominations for the most innovative 
              and high-impact startups to be recognized for their excellence and contributions to the growing entrepreneurial ecosystem.
            </p>
            <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
              <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
                <div className="flex items-start gap-2 sm:gap-3 flex-1">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-xs sm:text-sm font-semibold text-gray-900">January 16 - February 13, 2026</p>
                </div>
                <div className="flex items-center gap-2 ml-6 sm:ml-0">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-xs sm:text-sm text-gray-700">Anytime</p>
                </div>
              </div>
              <div className="flex items-start gap-2 sm:gap-3">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <p className="text-xs sm:text-sm text-gray-700">Bulwagang Balagtas, 4th Floor NALLRC, PUP Sta. Mesa, Manila</p>
              </div>
            </div>
            <p className="text-xs sm:text-sm text-gray-500 mb-3 sm:mb-4">
              Deadline of Registration is on February 8, 2026
            </p>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <Link to="contact" onClick={() => window.dispatchEvent(new Event('highlight-footer'))} className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-2.5 border-2 border-gray-400 text-gray-700 rounded-full font-medium hover:border-gray-500 hover:bg-gray-50 transition text-xs sm:text-sm cursor-pointer text-center">
                Contact Us
              </Link>
              <a target="_blank" href="https://www.facebook.com/DOSTPUPPYLONTBI/posts/pfbid02oUqXJ1nVtYb2tS5cRZ6ucYRvtHnuFjWCHwMFPY5csQ6mctdCUc1qvp2do1zffw63l"
              className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-2.5 rounded-full font-medium transition text-xs sm:text-sm text-white text-center"
                style={{
                  borderImage: 'linear-gradient(90deg, rgba(109,12,34,1) 0%, rgba(14,56,107,1) 76%) 1',
                  background: 'linear-gradient(90deg, rgba(109,12,34,1) 0%, rgba(14,56,107,1) 76%)',
                }}
              >
                Learn More
              </a>
            </div>
          </div>
        </div>
        </>
      );
    }

    if (activeTab === 'tech-based') {
      return (
        <>
          {/* Prototyping Program */}
          <div className="flex flex-col md:flex-row gap-4 sm:gap-6 md:gap-8 items-start" data-aos="fade-up" data-aos-delay="100">
            <div className="w-full md:w-80 flex-shrink-0 rounded-xl md:rounded-2xl overflow-hidden shadow-lg" data-aos="zoom-in" data-aos-delay="200">
              <img
                src="/images/tech-prototype.png"
                alt="PROTOTYPING PROGRAM"
                className="w-full h-48 sm:h-64 md:h-auto object-cover"
              />
            </div>
            <div className="flex-1" data-aos="fade-left" data-aos-delay="300">
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">
                PROTOTYPING PROGRAM
              </h3>
              <p className="text-xs sm:text-sm text-gray-600 mb-4 sm:mb-6 leading-relaxed">
                We provide best-practice assistance approach to hardware and software development, grounded on rapid iteration. This program utilize agile prototyping to transform conceptual sketches into functional Minimum Viable Products (MVPs) ready for pilot testing.
              </p>
              <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
                  <div className="flex items-start gap-2 sm:gap-3 flex-1">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-xs sm:text-sm font-semibold text-gray-900">Coming Soon</p>
                  </div>
                  <div className="flex items-center gap-2 ml-6 sm:ml-0">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-xs sm:text-sm text-gray-700">TBA</p>
                  </div>
                </div>
                <div className="flex items-start gap-2 sm:gap-3">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <p className="text-xs sm:text-sm text-gray-700">ICTO Computer Laboratory, PUP Sta. Mesa</p>
                </div>
              </div>
              <p className="text-xs sm:text-sm text-gray-500 mb-3 sm:mb-4">
                Stay tuned on our Facebook page for the latest news!
              </p>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <Link to="contact" onClick={() => window.dispatchEvent(new Event('highlight-footer'))} className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-2.5 border-2 border-gray-400 text-gray-700 rounded-full font-medium hover:border-gray-500 hover:bg-gray-50 transition text-xs sm:text-sm cursor-pointer text-center">
                  Contact Us
                </Link>
                <a 
                  href="#"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-2.5 rounded-full font-medium transition text-xs sm:text-sm text-white text-center"
                  style={{
                    borderImage: 'linear-gradient(90deg, rgba(109,12,34,1) 0%, rgba(14,56,107,1) 76%) 1',
                    background: 'linear-gradient(90deg, rgba(109,12,34,1) 0%, rgba(14,56,107,1) 76%)',
                  }}
                >
                  Learn More
                </a>
              </div>
            </div>
          </div>

          {/* TECH CONSULTATION */}
          <div className="flex flex-col md:flex-row gap-4 sm:gap-6 md:gap-8 items-start" data-aos="fade-up" data-aos-delay="400">
            <div className="w-full md:w-80 flex-shrink-0 rounded-xl md:rounded-2xl overflow-hidden shadow-lg" data-aos="zoom-in" data-aos-delay="500">
              <img
                src="/images/tech-consultation.png"
                alt="Tech Consultation"
                className="w-full h-48 sm:h-64 md:h-auto object-cover"
              />
            </div>
            <div className="flex-1" data-aos="fade-left" data-aos-delay="600">
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">
                TECH CONSULTATION
              </h3>
              <p className="text-xs sm:text-sm text-gray-600 mb-4 sm:mb-6 leading-relaxed">
                We provide professional guidance on software and hardware architecture. Our team helps startups navigate complex system integration, scalable cloud infrastructure, and custom hardware development, ensuring your technical foundation is robust and future-proof.
              </p>
              <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
                  <div className="flex items-start gap-2 sm:gap-3 flex-1">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-xs sm:text-sm font-semibold text-gray-900">Coming Soon</p>
                  </div>
                  <div className="flex items-center gap-2 ml-6 sm:ml-0">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-xs sm:text-sm text-gray-700">TBA</p>
                  </div>
                </div>
                <div className="flex items-start gap-2 sm:gap-3">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <p className="text-xs sm:text-sm text-gray-700">TBIDO Office</p>
                </div>
              </div>
              <p className="text-xs sm:text-sm text-gray-500 mb-3 sm:mb-4">
                Stay tuned on our Facebook page for the latest news!
              </p>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <Link to="contact" onClick={() => window.dispatchEvent(new Event('highlight-footer'))} className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-2.5 border-2 border-gray-400 text-gray-700 rounded-full font-medium hover:border-gray-500 hover:bg-gray-50 transition text-xs sm:text-sm cursor-pointer text-center">
                  Contact Us
                </Link>
                <a target="_blank" href="https://www.facebook.com/DOSTPUPPYLONTBI"
                  className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-2.5 rounded-full font-medium transition text-xs sm:text-sm text-white text-center"
                  style={{
                    borderImage: 'linear-gradient(90deg, rgba(109,12,34,1) 0%, rgba(14,56,107,1) 76%) 1',
                    background: 'linear-gradient(90deg, rgba(109,12,34,1) 0%, rgba(14,56,107,1) 76%)',
                  }}
                >
                  Learn More
                </a>
              </div>
            </div>
          </div>

        {/* TECH-NEXT-GEN */}
          <div className="flex flex-col md:flex-row gap-4 sm:gap-6 md:gap-8 items-start" data-aos="fade-up" data-aos-delay="400">
            <div className="w-full md:w-80 flex-shrink-0 rounded-xl md:rounded-2xl overflow-hidden shadow-lg" data-aos="zoom-in" data-aos-delay="500">
              <img
                src="/images/tech-next-gen.png"
                alt="Tech Next Gen"
                className="w-full h-48 sm:h-64 md:h-auto object-cover"
              />
            </div>
            <div className="flex-1" data-aos="fade-left" data-aos-delay="600">
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">
                TECH-NEXT-GEN
              </h3>
              <p className="text-xs sm:text-sm text-gray-600 mb-4 sm:mb-6 leading-relaxed">
                Tech Next Gen is our premier awareness event dedicated to emerging technologies. We bring together industry experts and innovators to explore the latest trends in AI, IoT, and high-performance computing, fostering a culture of technical curiosity and advancement.
              </p>
              <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
                  <div className="flex items-start gap-2 sm:gap-3 flex-1">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-xs sm:text-sm font-semibold text-gray-900">Coming Soon</p>
                  </div>
                  <div className="flex items-center gap-2 ml-6 sm:ml-0">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-xs sm:text-sm text-gray-700">TBA</p>
                  </div>
                </div>
                <div className="flex items-start gap-2 sm:gap-3">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <p className="text-xs sm:text-sm text-gray-700">Bulwagang Balagtas, 4th Floor NALLRC, PUP Sta. Mesa, Manila</p>
                </div>
              </div>
              <p className="text-xs sm:text-sm text-gray-500 mb-3 sm:mb-4">
                Stay tuned on our Facebook page for the latest news!
              </p>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <Link to="contact" onClick={() => window.dispatchEvent(new Event('highlight-footer'))} className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-2.5 border-2 border-gray-400 text-gray-700 rounded-full font-medium hover:border-gray-500 hover:bg-gray-50 transition text-xs sm:text-sm cursor-pointer text-center">
                  Contact Us
                </Link>
                <a target="_blank" href="https://www.facebook.com/DOSTPUPPYLONTBI"
                  className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-2.5 rounded-full font-medium transition text-xs sm:text-sm text-white text-center"
                  style={{
                    borderImage: 'linear-gradient(90deg, rgba(109,12,34,1) 0%, rgba(14,56,107,1) 76%) 1',
                    background: 'linear-gradient(90deg, rgba(109,12,34,1) 0%, rgba(14,56,107,1) 76%)',
                  }}
                >
                  Learn More
                </a>
              </div>
            </div>
          </div>

        </>
      );
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section with Gradient Background */}
      <section
        className="relative text-white overflow-hidden pt-16 sm:pt-20 md:pt-24 pb-[320px] sm:pb-[400px] md:pb-[450px] lg:pb-[300px] bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "url('/images/bg-2.png')" }}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 md:px-8 mt-6 sm:mt-8 md:mt-10">
          {/* Hero Text */}
          <div className="text-center mb-6 sm:mb-8" data-aos="fade-up">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-normal mb-3 sm:mb-4 px-2">
              Experience <span className="font-bold">Growth</span> Through <span className="font-bold">Innovation</span>
            </h1>
            <p className="text-xs sm:text-sm md:text-base font-light text-gray-200 max-w-lg mx-auto mb-4 sm:mb-6 px-4">
              Join programs designed to build your skills, strengthen your team, and expand your network.
            </p>
            <button className="bg-white text-xs sm:text-sm text-gray-700 px-6 sm:px-8 py-1.5 sm:py-2 rounded-full font-normal hover:bg-gray-100 transition">
              Your Startup journey Begins Here.
            </button>
          </div>

          {/* Images & Stats Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 auto-rows-auto gap-3 sm:gap-2 max-w-5xl mx-auto" data-aos="fade-up" data-aos-delay="200">
            
            {/* img-4 — WIDER & RECTANGULAR */}
            <div className="rounded-lg sm:rounded-xl overflow-hidden shadow-xl sm:shadow-2xl col-span-2 sm:col-span-3 h-40 sm:h-64 lg:col-span-2 lg:row-span-2 lg:h-full" data-aos="zoom-in" data-aos-delay="300">
              <img src="/images/img-4.png" alt="Large Left" className="w-full h-full object-cover" />
            </div>

            {/* img-5 — SMALL */}
            <div className="rounded-lg sm:rounded-xl overflow-hidden shadow-xl sm:shadow-2xl col-span-1 sm:col-span-1 h-32 sm:h-32 lg:h-full" data-aos="zoom-in" data-aos-delay="400">
              <img src="/images/img-5.png" alt="Small img 5" className="w-full h-full object-cover" />
            </div>

            {/* 80+ Stat */}
            <div className="rounded-2xl sm:rounded-3xl p-3 sm:p-4 flex flex-col items-center justify-center col-span-1 sm:col-span-1 h-32 sm:h-32 lg:h-full" data-aos="fade-up" data-aos-delay="500">
              <h3 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-1">80+</h3>
              <p className="text-[10px] sm:text-xs md:text-sm text-center font-light leading-tight">Collaboration with Industry and Innovative Partners</p>
            </div>

            {/* img-6 — Tall Image */}
            <div className="rounded-lg sm:rounded-xl overflow-hidden shadow-xl sm:shadow-2xl col-span-2 sm:col-span-1 h-40 sm:h-full lg:row-span-2" data-aos="zoom-in" data-aos-delay="600">
              <img src="/images/img-6.png" alt="Large Right" className="w-full h-full object-cover" />
            </div>

            {/* 5000+ Stat */}
            <div className="rounded-lg sm:rounded-xl p-3 sm:p-4 flex flex-col justify-center items-center col-span-1 sm:col-span-1 h-32 sm:h-32 lg:h-full" data-aos="fade-up" data-aos-delay="700">
              <p className="text-[10px] sm:text-xs md:text-sm text-center font-light leading-tight mb-1">Total Participants Joined and still counting!</p>
              <h3 className="text-2xl sm:text-3xl md:text-5xl font-bold">5000+</h3>
            </div>

            {/* img-1 — SMALLER */}
            <div className="rounded-2xl sm:rounded-3xl overflow-hidden shadow-xl sm:shadow-2xl col-span-1 sm:col-span-1 h-32 sm:h-28 lg:h-full" data-aos="zoom-in" data-aos-delay="800">
              <img src="/images/img-1.png" alt="Small img 1" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      <div id="programs-section"></div>

      {/* White Offset Container */}
      <section className="relative -mt-[280px] sm:-mt-[360px] md:-mt-[400px] lg:-mt-[220px] pb-12 sm:pb-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl sm:shadow-2xl p-6 sm:p-8 md:p-10 lg:p-12" data-aos="fade-up">
            
            {/* Header with GRADIENT */}
            <div className="text-center mb-6 sm:mb-8 md:mb-10" data-aos="fade-down" data-aos-delay="100">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-medium text-gray-800 mb-3 sm:mb-4">
                <span
                  className="font-bold text-transparent bg-clip-text"
                  style={{
                    backgroundImage: 'linear-gradient(90deg, rgba(109,12,34,1) 0%, rgba(14,56,107,1) 76%)',
                  }}
                >
                  Check out our Available Programs
                </span>
              </h2>
              <p className="text-xs sm:text-sm md:text-base py-1 text-gray-600 max-w-xl mx-auto px-2">
                Programs crafted to help founders and future innovators to validate ideas, grow their ventures, 
                enhance tech capabilities, and expand their networks.
              </p>
            </div>

            {/* Program Tabs with GRADIENT on Active */}
            <div className="max-w-3xl mx-auto mb-8 sm:mb-10 md:mb-12">
              <div className="grid grid-cols-2 sm:flex sm:flex-wrap sm:justify-center gap-3 sm:gap-6 md:gap-8 lg:gap-12 border-b-2 border-gray-200">
                {[
                  { id: 'pre-incubation', label: 'Pre-Incubation' },
                  { id: 'incubation', label: 'Incubation' },
                  { id: 'tech-based', label: 'Tech-Based' },
                  { id: 'networking', label: 'Networking' }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className="relative text-center cursor-pointer pb-3 sm:pb-4 px-1 sm:px-2 transition group"
                  >
                    <p 
                      className={`text-xs sm:text-sm md:text-base font-semibold whitespace-nowrap ${
                        activeTab === tab.id ? 'text-transparent bg-clip-text' : 'text-gray-500'
                      }`}
                      style={activeTab === tab.id ? {
                        backgroundImage: 'linear-gradient(90deg, rgba(109,12,34,1) 0%, rgba(14,56,107,1) 76%)',
                      } : {}}
                    >
                      {tab.label}
                    </p>
                    <p 
                      className={`text-xs sm:text-sm md:text-base font-semibold whitespace-nowrap ${
                        activeTab === tab.id ? 'text-transparent bg-clip-text' : 'text-gray-500'
                      }`}
                      style={activeTab === tab.id ? {
                        backgroundImage: 'linear-gradient(90deg, rgba(109,12,34,1) 0%, rgba(14,56,107,1) 76%)',
                      } : {}}
                    >
                      Programs
                    </p>
                    {activeTab === tab.id && (
                      <div 
                        className="absolute bottom-0 left-0 right-0 h-0.5 sm:h-1 rounded-t-full"
                        style={{
                          background: 'linear-gradient(90deg, rgba(109,12,34,1) 0%, rgba(14,56,107,1) 76%)',
                        }}
                      ></div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Program Cards */}
            <div className="space-y-8 sm:space-y-10 md:space-y-12 w-full overflow-hidden">
              {renderPrograms()}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}


{/* Coming Soon Section */}
// return (
//         <div className="text-center py-8 sm:py-10 md:py-12" data-aos="fade-up">
//           <div className="max-w-md mx-auto px-4">
//             {/* Illustration */}
//             <div className="mb-6 sm:mb-8" data-aos="zoom-in" data-aos-delay="100">
//               <img
//                 src="/images/programs-coming-soon.png"
//                 alt="Coming Soon"
//                 className="w-40 sm:w-48 md:w-64 h-auto mx-auto"
//               />
//             </div>
            
//             {/* Text */}
//             <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-2 sm:mb-3" data-aos="fade-up" data-aos-delay="200">
//               More Programs are coming soon!
//             </h3>
//             <p className="text-xs sm:text-sm text-gray-600 max-w-2xl mx-auto" data-aos="fade-up" data-aos-delay="300">
//               We're expanding our offerings! New TBIDO programs are underway to better support your innovation and entrepreneurship journey.
//             </p>
//           </div>
//         </div>
//       );